import { ReactNode } from 'react';

import { cn } from '@/lib/utils';

export interface PillTab<T extends string | number> {
  value: T;
  label: ReactNode;
  /** Shown after the label (hidden when 0 or undefined). */
  count?: number;
  icon?: ReactNode;
  /** Shown but not choosable (a period the plan does not cover). */
  disabled?: boolean;
  /** What a click on a disabled pill does instead — say why, offer the way in. */
  onDisabledClick?: () => void;
}

/**
 * A filter bar of segmented pills (one chosen). The page-level control for
 * status filters and ranges — the same grammar as the support inbox's.
 */
export function PillTabs<T extends string | number>({
  value,
  options,
  onChange,
  ariaLabel,
  className,
}: {
  value: T;
  options: PillTab<T>[];
  onChange: (value: T) => void;
  ariaLabel?: string;
  className?: string;
}) {
  return (
    <div role="tablist" aria-label={ariaLabel} className={cn('inline-flex max-w-full gap-1 overflow-x-auto rounded-xl bg-muted p-1', className)}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={String(o.value)}
            type="button"
            role="tab"
            aria-selected={active}
            aria-disabled={o.disabled || undefined}
            onClick={() => (o.disabled ? o.onDisabledClick?.() : onChange(o.value))}
            className={cn(
              'flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs whitespace-nowrap transition-colors',
              active ? 'bg-background font-medium text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
              o.disabled && !active && 'text-muted-foreground/60 hover:text-muted-foreground',
            )}
          >
            {o.icon && <span className="[&_svg]:size-3.5">{o.icon}</span>}
            {o.label}
            {!!o.count && <span className="tabular-nums text-muted-foreground">{o.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
